
import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { productsData } from '../../../mocks/products';

interface FeaturedProductsProps {
  onQuickEnquiry: () => void;
}

// Top 6 best-selling products
const featuredProducts = [
  {
    ...productsData.find(p => p.id === 'laser-toner-cartridges')!,
    category: 'Laser Toner',
    displayName: 'Laser Toner Cartridges',
  },
  {
    ...productsData.find(p => p.id === 'generic-ink')!,
    category: 'Printer Ink',
    displayName: 'Generic Refill Ink',
  },
  {
    ...productsData.find(p => p.id === 'lamination-pouch')!,
    category: 'Lamination',
    displayName: 'Lamination Pouches',
  },
  {
    ...productsData.find(p => p.id === 'opc-drums')!,
    category: 'OPC Drums',
    displayName: 'OPC Drums',
  },
  {
    ...productsData.find(p => p.id === 'toner-powder')!,
    category: 'Toner Powder',
    displayName: 'Toner Powder',
  },
  {
    ...productsData.find(p => p.id === 'lamination-machine')!,
    category: 'Machines',
    displayName: 'Lamination Machine',
  },
];

// Image Carousel Component with hover auto-scroll
function ImageCarousel({ 
  images, 
  name, 
  isMobile = false 
}: { 
  images: string[]; 
  name: string; 
  isMobile?: boolean;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-scroll on hover (desktop only)
  useEffect(() => {
    if (!isMobile && isHovering && images.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
      }, 1000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isHovering, images.length, isMobile]);

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        setCurrentIndex((prev) => (prev + 1) % images.length);
      } else {
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
      }
    }
  };

  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsHovering(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsHovering(false);
      setCurrentIndex(0);
    }
  };

  return (
    <div
      className="relative w-full aspect-square bg-gray-50 overflow-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={isMobile ? handleTouchStart : undefined}
      onTouchMove={isMobile ? handleTouchMove : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
    >
      <img
        src={images[currentIndex]}
        alt={name}
        className={`w-full h-full object-cover transition-transform duration-500 ${
          isHovering && !isMobile ? 'scale-110' : ''
        }`}
      />
      
      {/* Dot indicators */}
      {images.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(idx);
              }}
              className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                idx === currentIndex
                  ? 'w-4 bg-teal-600'
                  : 'w-1.5 bg-white/70 hover:bg-white'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function FeaturedProducts({ onQuickEnquiry }: FeaturedProductsProps) {
  return (
    <section className="py-8 sm:py-10 lg:py-12 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12">
        {/* Section Header - Center Aligned */}
        <div className="text-center mb-8 lg:mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
            High-Demand Products
          </h2>
          <p className="text-base sm:text-lg text-gray-600 mb-4">
            Quick enquiry for popular items
          </p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-teal-700 font-semibold hover:text-teal-800 group whitespace-nowrap cursor-pointer text-sm sm:text-base"
          >
            View All Products
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </Link>
        </div>

        {/* Products Grid - Mobile Optimized */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-8 mb-8 lg:mb-12">
          {featuredProducts.map((product, index) => (
            <div
              key={index}
              className="group bg-white border border-gray-200 rounded-lg lg:rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 lg:hover:-translate-y-2"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Product Image Carousel - 1:1 Aspect Ratio */}
              <ImageCarousel 
                images={product.images} 
                name={product.displayName} 
                isMobile={window.innerWidth < 768}
              />

              {/* Product Info */}
              <div className="p-3 sm:p-4 lg:p-6 space-y-2 sm:space-y-3 lg:space-y-4">
                {/* Category Tag */}
                <span className="inline-block px-2 py-1 sm:px-3 bg-teal-100 text-teal-700 text-[10px] sm:text-xs font-semibold rounded-full">
                  {product.category}
                </span>

                {/* Product Name */}
                <h3 className="text-xs sm:text-sm lg:text-lg font-bold text-gray-900 line-clamp-2 min-h-[32px] sm:min-h-[40px] lg:min-h-[56px]">
                  {product.displayName}
                </h3>

                {/* Availability */}
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-green-500"></div>
                  <span className="text-[10px] sm:text-xs lg:text-sm text-green-600 font-medium">{product.availability}</span>
                </div>

                {/* Pricing Info */}
                <p className="text-[10px] sm:text-xs lg:text-sm text-gray-500">Quote-based pricing</p>

                {/* CTA Button */}
                <button
                  onClick={onQuickEnquiry}
                  className="w-full px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3 border-2 border-teal-700 text-teal-700 hover:bg-teal-700 hover:text-white font-semibold rounded-md lg:rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-[11px] sm:text-xs lg:text-base"
                >
                  Quick Enquiry
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
